import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { GlassCard } from "@/components/GlassCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { KeyRound, Link2, Trash2, UserPlus, Webhook } from "lucide-react";

const ROLES = ["admin", "champion", "owner", "user"];
const DEFAULT_SCOPES = "wi:read, wi:execute";
const DEFAULT_EVENTS = "procedure.published, execution.started, step.completed, execution.finished";

export default function Admin() {
  const [users, setUsers] = useState([]);
  const [keys, setKeys] = useState([]);
  const [hooks, setHooks] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "user", department: "" });
  const [keyForm, setKeyForm] = useState({ name: "", scopes: DEFAULT_SCOPES });
  const [hookForm, setHookForm] = useState({ url: "", events: DEFAULT_EVENTS, secret: "" });
  const [newKeyReveal, setNewKeyReveal] = useState(null);
  const [err, setErr] = useState("");
  const [intErr, setIntErr] = useState("");

  const loadUsers = async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  };

  const loadIntegrations = async () => {
    const [k, w] = await Promise.all([
      api.get("/v1/integrations/api-keys"),
      api.get("/v1/integrations/webhooks"),
    ]);
    setKeys(k.data);
    setHooks(w.data);
  };

  useEffect(() => {
    loadUsers();
    loadIntegrations().catch(() => setIntErr("Could not load integrations"));
  }, []);

  const create = async () => {
    setErr("");
    try {
      await api.post("/users", form);
      setCreating(false);
      setForm({ name: "", email: "", password: "", role: "user", department: "" });
      loadUsers();
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Failed to create");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this user?")) return;
    await api.delete(`/users/${id}`);
    loadUsers();
  };

  const changeRole = async (id, role) => {
    await api.patch(`/users/${id}`, { role });
    loadUsers();
  };

  const createKey = async () => {
    setIntErr("");
    try {
      const { data } = await api.post("/v1/integrations/api-keys", {
        name: keyForm.name.trim(),
        scopes: keyForm.scopes.split(",").map((s) => s.trim()).filter(Boolean),
      });
      setNewKeyReveal(data.api_key);
      setKeyForm({ name: "", scopes: DEFAULT_SCOPES });
      loadIntegrations();
    } catch (e) {
      setIntErr(formatApiError(e?.response?.data?.detail) || "Failed to create API key");
    }
  };

  const revokeKey = async (id) => {
    if (!window.confirm("Revoke this API key?")) return;
    await api.delete(`/v1/integrations/api-keys/${id}`);
    loadIntegrations();
  };

  const createHook = async () => {
    setIntErr("");
    try {
      await api.post("/v1/integrations/webhooks", {
        url: hookForm.url.trim(),
        events: hookForm.events.split(",").map((s) => s.trim()).filter(Boolean),
        secret: hookForm.secret || null,
        active: true,
      });
      setHookForm({ url: "", events: DEFAULT_EVENTS, secret: "" });
      loadIntegrations();
    } catch (e) {
      setIntErr(formatApiError(e?.response?.data?.detail) || "Failed to create webhook");
    }
  };

  const deleteHook = async (id) => {
    if (!window.confirm("Delete this webhook?")) return;
    await api.delete(`/v1/integrations/webhooks/${id}`);
    loadIntegrations();
  };

  const toggleHook = async (h) => {
    await api.patch(`/v1/integrations/webhooks/${h.id}`, { active: !h.active });
    loadIntegrations();
  };

  return (
    <div className="space-y-8" data-testid="admin-page">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-white">Admin</h1>
          <p className="text-[#9AA4B2] text-sm mt-1">Users, API keys, and webhooks.</p>
        </div>
        <Button onClick={() => setCreating(true)} className="btn-primary-glow rounded-full" data-testid="admin-new-user-btn">
          <UserPlus size={14} className="mr-1.5" /> New user
        </Button>
      </header>

      <GlassCard className="p-0 overflow-hidden">
        <table className="w-full text-sm" data-testid="admin-users-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-[#5B6472] border-b border-white/10">
              <th className="py-3 px-5">Name</th>
              <th className="py-3 px-5">Email</th>
              <th className="py-3 px-5">Department</th>
              <th className="py-3 px-5">Role</th>
              <th className="py-3 px-5 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-white/5 hover:bg-white/[0.03]">
                <td className="py-3 px-5 text-white">{u.name}</td>
                <td className="py-3 px-5 text-[#9AA4B2]">{u.email}</td>
                <td className="py-3 px-5 text-[#9AA4B2]">{u.department || "—"}</td>
                <td className="py-3 px-5">
                  <select
                    value={u.role}
                    onChange={(e) => changeRole(u.id, e.target.value)}
                    className="bg-white/[0.04] border border-white/10 rounded-full px-3 py-1 text-white text-xs focus:outline-none focus:border-cyan-400"
                    data-testid={`role-select-${u.id}`}
                  >
                    {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </td>
                <td className="py-3 px-5 text-right">
                  <button
                    onClick={() => remove(u.id)}
                    className="text-rose-300 hover:text-rose-200 p-1"
                    data-testid={`delete-user-${u.id}`}
                    aria-label="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </GlassCard>

      <section className="space-y-4" data-testid="admin-integrations">
        <div>
          <h2 className="font-display text-xl text-white flex items-center gap-2">
            <KeyRound size={18} /> API keys
          </h2>
          <p className="text-xs text-[#9AA4B2] mt-1">
            Use <code className="text-cyan-300">X-API-Key</code> or{" "}
            <code className="text-cyan-300">Authorization: ApiKey …</code> for agents / MES.
          </p>
        </div>

        {newKeyReveal && (
          <GlassCard className="p-4 border border-amber-400/30 bg-amber-500/10">
            <div className="text-xs uppercase tracking-widest text-amber-200">Copy now — shown once</div>
            <code className="mt-2 block break-all text-sm text-white" data-testid="admin-api-key-reveal">
              {newKeyReveal}
            </code>
            <Button
              className="mt-3 btn-ghost-glass rounded-full"
              onClick={() => {
                navigator.clipboard?.writeText(newKeyReveal);
                setNewKeyReveal(null);
              }}
            >
              Copied — dismiss
            </Button>
          </GlassCard>
        )}

        <GlassCard className="p-4 space-y-3">
          <div className="flex flex-col md:flex-row gap-2">
            <Input
              placeholder="Key name (e.g. WarrantIQ)"
              value={keyForm.name}
              onChange={(e) => setKeyForm({ ...keyForm, name: e.target.value })}
              className="bg-white/[0.04] border-white/10 rounded-full text-white"
              data-testid="admin-key-name"
            />
            <Input
              placeholder="Scopes (comma-separated)"
              value={keyForm.scopes}
              onChange={(e) => setKeyForm({ ...keyForm, scopes: e.target.value })}
              className="bg-white/[0.04] border-white/10 rounded-full text-white"
            />
            <Button onClick={createKey} className="btn-primary-glow rounded-full shrink-0" data-testid="admin-key-create">
              Create key
            </Button>
          </div>
          <div className="divide-y divide-white/5">
            {keys.map((k) => (
              <div key={k.id} className="py-3 flex items-center justify-between gap-3 text-sm">
                <div>
                  <div className="text-white">{k.name}</div>
                  <div className="text-[11px] text-[#5B6472]">
                    {k.key_prefix}… · {(k.scopes || []).join(", ")} ·{" "}
                    {k.active ? "active" : "revoked"}
                  </div>
                </div>
                {k.active && (
                  <button onClick={() => revokeKey(k.id)} className="text-rose-300 hover:text-rose-200" aria-label="Revoke">
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}
            {!keys.length && <div className="py-3 text-sm text-[#5B6472]">No API keys yet.</div>}
          </div>
        </GlassCard>

        <div className="pt-2">
          <h2 className="font-display text-xl text-white flex items-center gap-2">
            <Webhook size={18} /> Webhooks
          </h2>
          <p className="text-xs text-[#9AA4B2] mt-1">Outbound events for procedure publish and execution lifecycle.</p>
        </div>

        <GlassCard className="p-4 space-y-3">
          <div className="flex flex-col gap-2">
            <Input
              placeholder="https://hooks.example.com/sopflow"
              value={hookForm.url}
              onChange={(e) => setHookForm({ ...hookForm, url: e.target.value })}
              className="bg-white/[0.04] border-white/10 rounded-full text-white"
              data-testid="admin-hook-url"
            />
            <Input
              placeholder="Events (comma-separated)"
              value={hookForm.events}
              onChange={(e) => setHookForm({ ...hookForm, events: e.target.value })}
              className="bg-white/[0.04] border-white/10 rounded-full text-white"
            />
            <div className="flex flex-col md:flex-row gap-2">
              <Input
                placeholder="Optional shared secret"
                value={hookForm.secret}
                onChange={(e) => setHookForm({ ...hookForm, secret: e.target.value })}
                className="bg-white/[0.04] border-white/10 rounded-full text-white"
              />
              <Button onClick={createHook} className="btn-primary-glow rounded-full shrink-0" data-testid="admin-hook-create">
                <Link2 size={14} className="mr-1.5" /> Add webhook
              </Button>
            </div>
          </div>
          <div className="divide-y divide-white/5">
            {hooks.map((h) => (
              <div key={h.id} className="py-3 flex items-center justify-between gap-3 text-sm">
                <div className="min-w-0">
                  <div className="text-white truncate">{h.url}</div>
                  <div className="text-[11px] text-[#5B6472]">
                    {(h.events || []).join(", ")} · {h.active ? "active" : "paused"}
                    {h.has_secret ? " · signed" : ""}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => toggleHook(h)}
                    className="text-xs text-cyan-300 hover:text-cyan-200"
                  >
                    {h.active ? "Pause" : "Resume"}
                  </button>
                  <button onClick={() => deleteHook(h.id)} className="text-rose-300 hover:text-rose-200" aria-label="Delete">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
            {!hooks.length && <div className="py-3 text-sm text-[#5B6472]">No webhooks yet.</div>}
          </div>
        </GlassCard>

        {intErr && <div className="text-rose-400 text-sm">{intErr}</div>}
      </section>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="glass-strong bg-[#0A0E1A]/90 text-white border-white/10 rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-display">Add user</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white/[0.04] border-white/10 rounded-full text-white" data-testid="admin-new-name" />
            <Input placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white/[0.04] border-white/10 rounded-full text-white" data-testid="admin-new-email" />
            <Input placeholder="Password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-white/[0.04] border-white/10 rounded-full text-white" data-testid="admin-new-password" />
            <Input placeholder="Department" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="bg-white/[0.04] border-white/10 rounded-full text-white" />
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full bg-white/[0.04] border border-white/10 rounded-full px-4 py-2.5 text-white" data-testid="admin-new-role">
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            {err && <div className="text-rose-400 text-sm">{err}</div>}
            <Button onClick={create} className="btn-primary-glow rounded-full w-full" data-testid="admin-new-submit">Create</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
